import { z } from 'zod';
import { getEnvVar } from '../../utils/env';

const Progress = z.object({
  position: z.string(),
  date: z.string(),
  updateTime: z.string(),
});

const Completed = z.object({
  dates: z.array(z.string()),
  viewCount: z.number(),
  updateTime: z.string(),
});

const Data = z.object({
  assetId: z.string(),
  categoryId: z.string(),
  totalTime: z.string(),
  progress: Progress.optional(),
  completed: Completed.optional(),
});

export type ViewingHistoryType = z.infer<typeof Data>;

const ErrorInfo = z.object({
  title: z.string(),
  detail: z.string(),
  code: z.string(),
});

const responseValidator = z.object({
  data: z.array(Data).optional(),
  errors: z.array(ErrorInfo).optional(),
});

export const getVidoesInProgress = ({
  jwt,
  subprofileToken,
}: {
  jwt: string;
  subprofileToken: string;
}) => {
  // if we don't have a jwt, don't send any requests
  if (jwt?.length === 0) {
    console.error('No JWT found');
    return Promise.resolve(undefined);
  }
  if (subprofileToken?.length === 0) {
    console.error('No subprofile token found');
    return Promise.resolve(undefined);
  }

  // call viewing history API based on the environment
  if (getEnvVar('NEXT_PUBLIC_ENABLE_VIEWING_HISTORY') !== 'true') {
    return Promise.resolve([]);
  }

  const headers = {
    'content-type': 'application/json',
    accept: 'application/json',
    authorization: `Bearer ${jwt}`,
    'X-Vimond-Subprofile': subprofileToken,
  };

  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };
  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.viewing-history.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/viewinghistory/progress?maxResults=20&skipOlderThan=P1M`,
    requestOptions,
  )
    .then((res) => {
      return res.json();
    })
    .then((json) => {
      const response = responseValidator.parse(json);
      if (response.errors) {
        console.error(response.errors[0]?.detail);
      }
      return response.data;
    });
};

export const deleteVideoProgress = ({
  jwt,
  subprofileToken,
  assetId,
}: {
  jwt: string;
  subprofileToken: string;
  assetId: string;
}) => {
  // if we don't have a jwt, don't send any requests
  if (jwt?.length === 0) {
    console.error('No JWT found');
    return Promise.resolve(undefined);
  }
  if (subprofileToken?.length === 0) {
    console.error('No subprofile token found');
    return Promise.resolve(undefined);
  }

  const headers = {
    'content-type': 'application/json',
    accept: 'application/json',
    authorization: `Bearer ${jwt}`,
    'X-Vimond-Subprofile': subprofileToken,
  };

  const requestOptions: RequestInit = {
    method: 'DELETE',
    headers,
    redirect: 'follow',
  };

  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.viewing-history.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/viewinghistory/${assetId}`,
    requestOptions,
  );
};
